import React from 'react';
import { RecoilRoot } from 'recoil';
import * as Sentry from '@sentry/react';
import { QueryParamProvider } from 'use-query-params';
import { Redirect, Route, Router } from 'react-router-dom';
import AuthenticatedScopeApp from './main/AuthenticatedScopeApp';
import {
  APP,
  APP_TASKS,
  EXTERNAL_ACTION,
  HOME,
  LOGIN,
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
} from './_constants/routes';
import SessionManagerFactory from '../misc/session';
import { TermsAndConditions } from './termsAndConditions';
import { PrivacyPolicy } from './privacyPolicy';
import VersionOutdated from '../components/versionOutdated/versionOutdated';
import routerHistory from './history';
import LoginPage from '../pages/loginPage/loginPage';
import styles from './app.module.css';
import { useLogin } from '../hooks/useLogin';
import { ExternalActions } from '../pages/externalActionsPages/externalActions';

const SessionManager = SessionManagerFactory();

const PrivateRoute = ({ isLogged, component: Component, ...rest }) => (
  <Route
    {...rest}
    render={props =>
      isLogged ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{
            pathname: LOGIN,
            state: { from: props.location },
          }}
        />
      )
    }
  />
);

const AppContent = () => {
  const { isLogged } = useLogin();

  return (
    <div className={styles.app}>
      <Route exact path={TERMS_AND_CONDITIONS} component={TermsAndConditions} />
      <Route exact path={PRIVACY_POLICY} component={PrivacyPolicy} />
      <Route
        exact
        path={HOME}
        render={props => (
          <Redirect
            to={{
              pathname: APP,
              state: { from: props.location },
            }}
          />
        )}
      />
      <PrivateRoute isLogged={isLogged} path={APP} component={AuthenticatedScopeApp} />
      <Route
        path={LOGIN}
        render={props => {
          if (SessionManager.hasEmptySession()) {
            return <LoginPage isLogged={isLogged} {...props} />;
          }
          const stateFrom = props.location?.state?.from;
          if (stateFrom) {
            stateFrom.search = '';
          }
          const redirectTo = stateFrom && stateFrom !== LOGIN ? stateFrom : APP_TASKS;
          return <Redirect to={redirectTo} />;
        }}
      />
      <Route path={EXTERNAL_ACTION} component={ExternalActions} />
    </div>
  );
};

const App = () => (
  <RecoilRoot key={SessionManager.getUser()?.id}>
    <Router history={routerHistory}>
      <QueryParamProvider ReactRouterRoute={Route}>
        <AppContent />
      </QueryParamProvider>
    </Router>
    <VersionOutdated />
  </RecoilRoot>
);

export default Sentry.withProfiler(App);
